import {
  cloneElement,
  Fragment,
  isValidElement,
  MouseEvent,
  PropsWithChildren,
  useCallback,
  useMemo,
} from 'react';
import { Popover, PopoverProps } from '@mui/material';
import { bindTrigger, bindPopover } from 'material-ui-popup-state';
import { PopupState } from 'material-ui-popup-state/hooks';

import ErrorBoundary from '../../ErrorBoundary/ErrorBoundary';
import { ShowComponentIfUserHasPermission } from 'RBAC/index';
import ControlBarSubButton from '@3.0/Buttons/ControlBarButtons/ControlBarSubButton';
import './DropDownStyles.css';
import { IPermissionRef } from 'RBAC/constants/interfaces';

export type DropDownRef = {
  close: () => void;
};

export type TDropDown = {
  label: string | React.ReactNode;
  popOverProps?: Partial<PopoverProps>;
  containerBorders: {
    left: boolean;
    right: boolean;
  };
  popupState: PopupState;
} & IPermissionRef &
  PropsWithChildren;

const DropDown = ({
  popOverProps = {},
  label,
  children,
  permissionRef,
  containerBorders = {
    left: true,
    right: true,
  },
  popupState,
}: TDropDown) => {
  const handleClose = useCallback(
    (event: {}, reason: 'backdropClick' | 'escapeKeyDown') => {
      popupState.close();

      if (popOverProps?.onClose) {
        popOverProps.onClose(event, reason);
      }
    },
    [popOverProps, popupState]
  );

  const handleToggle = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      popupState.toggle(event.currentTarget);
    },
    [popupState]
  );

  const childWithProps = useMemo(() => {
    if (!isValidElement(children) || typeof children.type === 'string') {
      return children;
    } else {
      return cloneElement(children as React.ReactElement<any>, {
        ...children.props,
        closeDropDown: handleClose,
      });
    }
  }, [children, handleClose]);

  return (
    <Fragment>
      <ShowComponentIfUserHasPermission permissionRef={permissionRef}>
        <ControlBarSubButton
          containerBorders={containerBorders}
          isOpen={popupState.isOpen}
          handleToggle={handleToggle}
          label={label}
          {...bindTrigger(popupState)}
        />
        <Popover
          {...popOverProps}
          {...bindPopover(popupState)}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          className={popOverProps?.className || 'dropdown-popover'}
        >
          <ErrorBoundary accessDeniedWidth={'w-90'} containerClass="pa-24">
            {childWithProps}
          </ErrorBoundary>
        </Popover>
      </ShowComponentIfUserHasPermission>
    </Fragment>
  );
};

export default DropDown;
